import {QueryBuilderColumn, QueryBuilderQueryRuleListValueEntry, QueryBuilderReducerActionTypeEnum, QueryBuilderReducerDispatch, QueryBuilderStateQueryRule} from "../../../../index";
import React, {FunctionComponent, useCallback, useEffect, useLayoutEffect, useMemo, useState} from "react";
import {Autocomplete, AutocompleteProps, Box, Checkbox, Chip, CircularProgress, createFilterOptions, Skeleton, TextField} from "@mui/material";
import axios, {CancelToken} from "axios";
import useIsMounted from "../../../hooks/use-is-mounted";
import QueryBuilderApiService from "../../../../core/services/api";

export interface QueryBuilderValueInputListProps {
    rule: QueryBuilderStateQueryRule;
    ruleIndex: number;
    conditionIndex: number;
    disabled: boolean;
    dispatch: QueryBuilderReducerDispatch;
    column: QueryBuilderColumn;
}

const QueryBuilderValueInputList: FunctionComponent<QueryBuilderValueInputListProps> = ({
                                                                                            dispatch,
                                                                                            disabled,
                                                                                            rule,
                                                                                            column,
                                                                                            ruleIndex,
                                                                                            conditionIndex,
                                                                                        }) => {
    const [options, setOptions] = useState<Array<QueryBuilderQueryRuleListValueEntry>>([]);
    const useApi = useMemo(() => !!column.apiUrl, [column.apiUrl]);
    const [loading, setLoading] = useState(useApi);
    const isMounted = useIsMounted();


    const value = useMemo(() => {
        if (column.multiSelect) {
            return rule.value as Array<QueryBuilderQueryRuleListValueEntry> ?? [];
        }
        return rule.value as QueryBuilderQueryRuleListValueEntry ?? null;
    }, [column.multiSelect, rule.value]);

    /**
     * With each change in the [column.options] prop:
     * - updates the options.
     */
    useLayoutEffect(() => {
        if (useApi)
            return;
        setOptions(() => {
            const options = column.options?.map((option) => {
                if (typeof option === "string" || typeof option === "number") {
                    return {
                        value: option.toString(),
                        key: option,
                    }
                }
                return option;
            }) ?? []
            const uniqueOptionKeys = [...new Set(options.map(e => e.key) ?? []).values()];
            return uniqueOptionKeys.map((key) => options.find(e => e.key === key)!);
        })
    }, [column.options, useApi]);

    /**
     * With each change in the [useApi] prop:
     * - updates the loading state.
     */
    useLayoutEffect(() => {
        setLoading(useApi)
    }, [useApi]);

    /**
     * Fetches the options from the api.
     */
    const fetchOptions = useCallback(async (cancelToken: CancelToken) => {
        setLoading(true);
        const response = await QueryBuilderApiService.getFilterOptions(
            column.baseUrl!,
            column.apiUrl!,
            undefined,
            cancelToken
        );
        if (!isMounted())
            return;
        if (!response?.aborted) {
            setLoading(false);
        }
        if (response?.resultFlag) {
            const uniqueOptionKeys = [...new Set(response.data?.map(e => e.key) ?? []).values()];
            setOptions(uniqueOptionKeys.map((key) => response.data?.find(e => e.key === key)!));
        }
    }, [column.apiUrl, column.baseUrl, isMounted]);

    /**
     * With each change in the [useApi] prop: (basically as soon as the component is mounted and the column has an api url)
     * - fetches the options.
     */
    useEffect(() => {
        if (!useApi)
            return;
        const cancelTokenSource = axios.CancelToken.source();
        fetchOptions(cancelTokenSource.token).then();
        return () => cancelTokenSource.cancel();
    }, [fetchOptions, useApi]);

    /**
     * Commits the rule's value in the reducer for string types.
     */
    const commitValue = useCallback<Required<AutocompleteProps<QueryBuilderQueryRuleListValueEntry, boolean, any, any>>['onChange']>((e, value) => {
        dispatch({
            type: QueryBuilderReducerActionTypeEnum.ChangeValue,
            payload: {
                conditionIndex: conditionIndex,
                ruleIndex: ruleIndex,
                value: value as any,
            },
        })
    }, [dispatch, conditionIndex, ruleIndex]);

    const filterOptions = useMemo(() => createFilterOptions<QueryBuilderQueryRuleListValueEntry>({
        ignoreCase: true,
        limit: 100,
        stringify: (option) => option.value ?? ""
    }), []);

    return (
        <>
            <Autocomplete<QueryBuilderQueryRuleListValueEntry, boolean>
                fullWidth
                options={options}
                disabled={disabled}
                readOnly={disabled}
                multiple={column.multiSelect ?? false}
                value={value}
                onChange={commitValue}
                size={'small'}
                limitTags={1}
                filterOptions={(options, state) => filterOptions(options, state)}
                isOptionEqualToValue={(option, value) => option.key === value?.key}
                getOptionLabel={(option) => (option?.value ?? "").concat(`(${option.key?.toString() ?? ""})`)}
                renderOption={(props, option, state) => (
                    <Box component={'li'} {...props} sx={{display: 'flex', justifyContent: 'space-between', gap: 1}}>
                        <Checkbox size={'small'} readOnly checked={state.selected}/>
                        {option.value ?? ""}
                    </Box>
                )}
                componentsProps={{
                    clearIndicator: {color: 'default'},
                    popupIndicator: {color: 'default'},
                }}
                renderInput={(params) =>
                    <TextField
                        {...params}
                        size={'small'}
                        label={'List Value'}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <React.Fragment>
                                    {loading ? <CircularProgress color="inherit" size={20}/> : null}
                                    {params.InputProps.endAdornment}
                                </React.Fragment>
                            ),
                        }}
                    />
                }
                renderTags={(value: QueryBuilderQueryRuleListValueEntry[], getTagProps) =>
                    value.map((option, index) => {
                        const {key, ...props} = getTagProps({index});
                        return (
                            <Chip key={key} variant="outlined" label={option.value} {...props} />
                        )
                    })
                }
                loading={loading}
                loadingText={
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                        {
                            Array.from({length: 4}).map((_, key) => (
                                <Skeleton
                                    key={key}
                                    variant="rectangular"
                                    width={'100%'}
                                    sx={(theme) => ({minHeight: 30, borderRadius: (theme as any).vars?.shape.borderRadius})}
                                />
                            ))
                        }
                    </Box>
                }
            />
        </>
    );
};


export default QueryBuilderValueInputList;
